import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { COMPANY_CONFIGS } from "@/config/companies";

export const useInnoventureTestDetails = (testId: string | undefined) => {
  return useQuery({
    queryKey: ['innoventure-test-details', testId],
    queryFn: async () => {
      if (!testId) throw new Error("Test ID is required");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Fetch test details with question count from view
      const { data: test, error: testError } = await supabase
        .from('tests_with_question_counts')
        .select(`
          id,
          title,
          description,
          job_title,
          welcome_message,
          time_limit,
          time_limit_type,
          total_time_limit,
          scheduled_start_time,
          scheduled_end_time,
          share_token,
          invitation_only,
          organization_id,
          question_count,
          total_question_time_seconds
        `)
        .eq('id', testId)
        .eq('organization_id', COMPANY_CONFIGS.innoventure.organizationId)
        .single();

      if (testError) throw testError;
      if (!test) throw new Error("Test not found or not part of Innoventure");

      // Check if user has already taken the test OR has in-progress test
      const { data: existingResponse, error: responseError } = await supabase
        .from('test_responses')
        .select('id, status, completed_at, last_question_index')
        .eq('test_id', testId)
        .eq('candidate_id', user.id)
        .in('status', ['completed', 'in_progress', 'abandoned'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (responseError) throw responseError;

      // Check scheduling status
      const now = new Date();
      const start = test.scheduled_start_time ? new Date(test.scheduled_start_time) : null;
      const end = test.scheduled_end_time ? new Date(test.scheduled_end_time) : null;
      
      const isScheduled = !!(start || end);
      const hasStarted = start ? now >= start : true;
      const hasEnded = end ? now > end : false;

      return {
        test,
        questionCount: test.question_count || 0,
        hasAlreadyTaken: existingResponse?.status === 'completed' || existingResponse?.status === 'abandoned',
        canResume: existingResponse?.status === 'in_progress',
        resumeFromQuestionIndex: existingResponse?.last_question_index || 0,
        isScheduled,
        hasStarted,
        hasEnded,
      };
    },
    enabled: !!testId,
  });
};
